import { Button } from "primereact/button";
import { InputText } from "primereact/inputtext";
import { Toast } from "primereact/toast";
import React from "react";
import { Link, Navigate } from "react-router-dom";
import { HandleAuthTokenRefreshTimer, safeJson } from "src/app/helpers/Index";
import { LoadClubContext, SetAdminRouteFromLocation } from "src/app/helpers/ClubContext";
import { LoginDetails } from "src/app/interfaces/Index";
import { AppSettingsService, AuthService, UserService } from "src/app/services/Index";
import { ClubService } from "src/app/services/ClubService";
import { environment } from "src/environments/environment";
import "./AdminLogin.css";

type ClubBrand = {
	name: string;
	slug: string;
	primaryColour: string;
	secondaryColour: string;
	badgeUrl: string;
};
type Props = {};
type State = {
	brand: ClubBrand | null;
	isLoading: boolean;
	loggedIn: boolean;
	loginDetails: LoginDetails;
};
export class AdminLogin extends React.Component<Props, State> {
	toast: any;

	constructor(props: Props) {
		super(props);
		this.state = {brand: null, isLoading: false, loggedIn: false, loginDetails: new LoginDetails()};
	}

	componentDidMount() {
		document.documentElement.classList.add("admin-zoom");
		SetAdminRouteFromLocation();
		this.getBranding();
		this.isUserLoggedIn();
	}

	/** Themes the login screen from the club slug in /my-club/{slug}. */
	/** Darken (negative) or lighten (positive) a #rrggbb by a percentage. */
	shade(hex: string, percent: number) {
		const clean = (hex || "").replace("#", "");
		if (clean.length !== 6) return hex;
		const amount = Math.round(2.55 * percent);
		const channel = (start: number) => {
			const value = parseInt(clean.substring(start, start + 2), 16) + amount;
			return Math.max(0, Math.min(255, value)).toString(16).padStart(2, "0");
		};
		return `#${channel(0)}${channel(2)}${channel(4)}`;
	}

	/** The club's colours as the page backdrop, mirroring the SUFC original. */
	backdrop(primary?: string) {
		if (!primary) return undefined;
		return `linear-gradient(135deg, ${this.shade(primary, -22)} 0%, ${this.shade(primary, -11)} 55%, ${primary} 100%)`;
	}

	async getBranding() {
		const match = window.location.pathname.match(/^\/my-club\/([^/]+)/);
		if (!match) return;
		try {
			const res = await ClubService.getBranding(match[1]);
			if (!res.ok) return;
			const brand = await res.json();
			if (brand && brand.slug) this.setState({brand});
		} catch { /* endpoint unavailable - default branding stands */ }
	}

	async isUserLoggedIn() {
		const res = await safeJson(UserService.loginCheck("Administrators"));
		if (res === true) {
			await LoadClubContext();
			this.setState({loggedIn: true});
			await HandleAuthTokenRefreshTimer();
		}
	}

	async login() {
		this.setState({isLoading: true});
		const res = await AuthService.login(this.state.loginDetails, "Administrators");
		if (!res.ok) {
			this.toast.show({closable: false, detail: "Please try again later.", life: 5000, severity: "error", summary: "Something went wrong!"});
			this.setState({isLoading: false});
		} else {
			const json = (await res.json());
			await AppSettingsService.set("accessToken", json.accessToken);
			await AppSettingsService.set("refreshToken", json.refreshToken);
			await AppSettingsService.set("groups", json.groups.toString());
			await LoadClubContext();
			this.setState({isLoading: false, loggedIn: true});
			await HandleAuthTokenRefreshTimer();
		}
	}

	componentWillUnmount() {
		document.documentElement.classList.remove("admin-zoom");
	}

	render() {
		const brand = this.state.brand;
		const primary = brand?.primaryColour || "#11B981";
		// Once we know which club this is, never fall back to another club's
		// badge — show the Pitchbase mark until they upload their own.
		const badge = brand
			? (brand.badgeUrl || "/assets/pitchbase-icon.png")
			: "/assets/Badge.png";
		const clubName = brand?.name || "Scunthorpe United";
		return (
			<>
				<div id="adminLogin" style={{ background: this.backdrop(brand?.primaryColour) }}>
					<div className="wrapper">
						<div className="panel">
							<div className="d-flex align-items-center justify-content-center" style={{gap: "0.6rem", marginBottom: "1.5rem"}}>
								<img alt="Pitchbase" src="/assets/pitchbase-icon.png" style={{borderRadius: 8, height: "2rem"}}/>
								<span style={{fontSize: "1.3rem", fontWeight: 800, letterSpacing: "0.02em"}}>Pitchbase</span>
							</div>
							<img alt={clubName} src={badge} style={{filter: "drop-shadow(0 8px 16px rgba(0, 0, 0, 0.15))", height: 110, marginBottom: "1.25rem", maxWidth: 180, objectFit: "contain"}}/>
							<h1 style={{fontSize: "1.5rem", fontWeight: 800, marginBottom: "0.25rem"}}>Welcome back</h1>
							<p style={{color: "#6B7280", fontWeight: 600, marginBottom: "1.75rem"}}>Sign in to manage {clubName}</p>
							<div style={{textAlign: "left", width: "100%"}}>
								<label htmlFor="emailAddress">Email</label>
								<InputText disabled={this.state.isLoading} id="emailAddress" onChange={(e) => this.setState({loginDetails: {...this.state.loginDetails, emailAddress: e.target.value}})} onKeyDown={(e) => e.key === "Enter" && this.login()} placeholder="you@club.co.uk" style={{marginBottom: "1rem", width: "100%"}} value={this.state.loginDetails.emailAddress} />
								<label htmlFor="password">Password</label>
								<InputText disabled={this.state.isLoading} id="password" onChange={(e) => this.setState({loginDetails: {...this.state.loginDetails, password: e.target.value}})} onKeyDown={(e) => e.key === "Enter" && this.login()} placeholder="••••••••" style={{marginBottom: "1.5rem", width: "100%"}} type="password" value={this.state.loginDetails.password} />
							</div>
							<Button label="Sign In" loading={this.state.isLoading} onClick={() => this.login()} style={{backgroundColor: primary, borderColor: primary, fontWeight: 700, height: "3.1rem", width: "100%"}} />
							<Link style={{color: primary, display: "block", fontWeight: 700, marginTop: "1.25rem", textDecoration: "none"}} to="/forgot-password">Forgot password?</Link>
							{this.state.loggedIn && (
								<Navigate replace={true} to={`/${environment.build.adminRoute}`} />
							)}
						</div>
						<div className="poweredBy">
							<img alt="" src="/assets/pitchbase-icon.png" style={{borderRadius: 5, height: "1.2rem"}}/>
							<span>Powered by <strong>Pitchbase</strong></span>
						</div>
					</div>
				</div>
				<Toast ref={(el) => this.toast = el}></Toast>
			</>
		);
	}
}